import { isPortrait } from "@multimediallc/web-utils"
import { isMobileDevice } from "@multimediallc/web-utils/modernizr"
import { EventRouter } from "../../../common/events"
import { isDesktopDmActive, isMobileDmActive } from "../../../common/featureFlagUtil"
import { openMessagingConversation } from "../../../common/mobilelib/userActionEvents"
import { screenOrientationChanged } from "../../../common/mobilelib/windowOrientation"
import { uuidv4 } from "../../../common/uuid"
import { createDmWindowRequest } from "../pm/dmWindowsManager"
import { ReactComponentRegistry } from "../ReactRegistry"
import { messagingProps } from "./messagingUtil"
import type { ReactComponent } from "../ReactRegistry"

export const openMessagingInbox = new EventRouter<undefined>("openMessagingInbox")
export const openMessagingUnified = new EventRouter<string>("openMessagingUnified")
export const messagingInboxClosed = new EventRouter<void>("messagingInboxClosed")

enum View {
    Mobile = "mobile",
    DesktopInbox = "desktop_inbox",
    DesktopConversation = "desktop_conversation",
    DesktopUnified = "desktop_unified",
}

type MountedComponent = {
    component: ReactComponent
    container: HTMLDivElement
}

type MessagingInstance = MountedComponent & {
    hidden: boolean;
    view: View;
    initialEntry?: string;
}

const FLEX_CONTAINER_RIGHT_OFFSET_DEFAULT = "8px"
const FLEX_CONTAINER_RIGHT_OFFSET_INBOX_CLOSED_CONVO_OPEN = "69px"
const FLEX_CONTAINER_GAP = "8px"

class MessagingManager {
    private instances = new Map<string, MessagingInstance>()
    private landscapeModal?: MountedComponent
    private orientationOpenPending = false
    private messagingFlexContainer: HTMLDivElement

    constructor() {
        // catch all condition to prevent new messaging for now
        if (!isMobileDmActive()) {
            return
        }

        this.bindListeners()
    }

    /** Create a flex container to hold desktop inbox and conversation instances if it doesn't already exist */
    private createMessagingFlexContainerIfNecessary(): void {
        if (this.messagingFlexContainer !== undefined) {
            return
        }
        this.messagingFlexContainer = document.createElement("div")
        this.messagingFlexContainer.id = "messaging-flex-container"
        this.messagingFlexContainer.style.position = "fixed"
        this.messagingFlexContainer.style.bottom = "0"
        this.messagingFlexContainer.style.right = FLEX_CONTAINER_RIGHT_OFFSET_DEFAULT
        this.messagingFlexContainer.style.display = "flex"
        this.messagingFlexContainer.style.flexDirection = "row-reverse"
        this.messagingFlexContainer.style.alignItems = "flex-end"
        this.messagingFlexContainer.style.gap = FLEX_CONTAINER_GAP
        this.messagingFlexContainer.style.pointerEvents = "none"
        document.body.appendChild(this.messagingFlexContainer)
    }

    /**
     * Adjusts the right position of the messagingFlexContainer based on whether
     * the desktop inbox or any desktop conversations are visible.
     */
    private adjustMessagingFlexContainerPosition(): void {
        let hasVisibleDesktopInbox = false
        let hasVisibleDesktopConversation = false

        for (const instance of this.instances.values()) {
            if (instance.hidden) {
                continue
            }

            if (instance.view === View.DesktopInbox) {
                hasVisibleDesktopInbox = true
            }

            if (instance.view === View.DesktopConversation) {
                hasVisibleDesktopConversation = true
            }
        }

        let targetRightOffset = FLEX_CONTAINER_RIGHT_OFFSET_DEFAULT

        if (!hasVisibleDesktopInbox && hasVisibleDesktopConversation) {
            targetRightOffset = FLEX_CONTAINER_RIGHT_OFFSET_INBOX_CLOSED_CONVO_OPEN
        }
        
        this.messagingFlexContainer.style.right = targetRightOffset
    }

    private isDesktopFlexView(view: View): boolean {
        return view === View.DesktopInbox || view === View.DesktopConversation
    }

    private bindListeners() {
        openMessagingInbox.listen(() => {
            this.createInboxInstance()
        })

        // mobile only event for now
        openMessagingConversation.listen((username: string) => {
            this.createInstance({
                view: View.Mobile,
                onClose: () => { this.destroyAll() },
                initialEntry: `/messages/${username}`,
                ...messagingProps,
            })
        })

        // opening a conversation from the desktop
        createDmWindowRequest.listen((username: string) => {
            const messagingInstanceId = `messaging-${username}`
            if(!isDesktopDmActive() || this.isInstanceVisible(messagingInstanceId)) {
                return
            }
            this.destroyAllDesktopConversations()
            this.createInstance({
                view: View.DesktopConversation,
                onClose: (messagingInstanceId: string) => { this.destroyInstance(messagingInstanceId) },
                initialEntry: `/messages/${username}`,
                messagingInstanceId,
                ...messagingProps,
            })
        })

        // desktop unified popout
        openMessagingUnified.listen((username: string) => {
            if(!isDesktopDmActive()) {
                return
            }
            this.destroyAll()
            this.createInstance({
                view: View.DesktopUnified,
                initialEntry: `/messages/${username ?? ""}`,
                onClose: () => {},
                ...messagingProps,
            })
        })

        screenOrientationChanged.listen(() => {
            if(this.landscapeModal && isPortrait()) {
                this.hideLandscapeModal()
            }

            if (this.instances.size === 0) {
                return
            }
            const instanceId = this.instances.keys().next().value
            if (instanceId === undefined) { return }
            const instance = this.instances.get(instanceId)
            if (instance === undefined) {return}

            if (!instance.hidden && !isPortrait()) {
                this.showLandscapeModal()
                this.hideInstance(instanceId)
                this.orientationOpenPending = true
            }

            if (this.orientationOpenPending && isPortrait()) {
                this.orientationOpenPending = false
                this.showInstance(instanceId)
            }
        })
    }

    /**
     * Creates and mounts a Messaging component instance.
     * For DesktopInbox and DesktopConversation views, it creates a new div and appends it to the messaging flex container.
     * For Mobile and DesktopUnified views, it attempts to use div#content as the host,
     * @param {object} props - Configuration properties for the messaging instance.
     * @returns {{ messagingInstanceId: string }} An object containing the ID of the created messaging instance.
     */
    createInstance(props: Record<string, unknown> & { messagingInstanceId?: string, view: View }): { messagingInstanceId: string } {
        const finalMessagingInstanceId = props.messagingInstanceId ?? `messaging-${uuidv4()}`
        let hostElementForReactComponent: HTMLDivElement

        if (this.isDesktopFlexView(props.view)) {
            // Regular desktop inbox and conversation views
            this.createMessagingFlexContainerIfNecessary()
            hostElementForReactComponent = document.createElement("div")
            hostElementForReactComponent.dataset.messagingId = finalMessagingInstanceId
            hostElementForReactComponent.style.pointerEvents = "auto"
            this.messagingFlexContainer.appendChild(hostElementForReactComponent)
        } else {
            // Mobile and DesktopUnified views
            const contentDiv = document.getElementById("content") as HTMLDivElement | null
            if (contentDiv) {
                hostElementForReactComponent = contentDiv
            } else {
                hostElementForReactComponent = document.createElement("div")
                hostElementForReactComponent.dataset.messagingId = finalMessagingInstanceId
                document.body.appendChild(hostElementForReactComponent)
            }
        }

        const MessagingClass = ReactComponentRegistry.get("Messaging")
        const messagingComponent = new MessagingClass({ ...messagingProps, ...props, messagingInstanceId: finalMessagingInstanceId }, hostElementForReactComponent)
        this.instances.set(finalMessagingInstanceId, { component: messagingComponent, container: hostElementForReactComponent, hidden: false, view: props.view })

        if (this.isDesktopFlexView(props.view)) {
            this.adjustMessagingFlexContainerPosition()
        }

        return { messagingInstanceId: finalMessagingInstanceId }
    }

    destroyInstance(id: string): void {
        const instance = this.instances.get(id)
        if (!instance) {return}

        const viewType = instance.view

        if (typeof instance.component.dispose === "function") {
            instance.component.dispose()
        }

        if (instance.container.parentElement) {
            instance.container.parentElement.removeChild(instance.container)
        }

        this.instances.delete(id)

        if (this.isDesktopFlexView(viewType)) {
            this.adjustMessagingFlexContainerPosition()
        }
    }

    isInstanceVisible(id: string): boolean {
        const instance = this.instances.get(id)
        return instance !== undefined && !instance.hidden
    }

    hideInstance(id: string): void {
        const instance = this.instances.get(id)
        if (!instance) {return}

        instance.container.style.display = "none"
        instance.hidden = true

        if (this.isDesktopFlexView(instance.view)) {
            this.adjustMessagingFlexContainerPosition()
        }
    }

    showInstance(id: string): void {
        const instance = this.instances.get(id)
        if (!instance) {return}

        instance.container.style.display = "block"
        instance.hidden = false

        if (this.isDesktopFlexView(instance.view)) {
            this.adjustMessagingFlexContainerPosition()
        }
    }

    destroyAll(): void {
        for (const [id] of this.instances) {
            this.destroyInstance(id)
        }
    }

    showLandscapeModal(): void {
        if (this.landscapeModal) {
            return
        }

        const container = document.createElement("div")
        document.body.appendChild(container)

        const onClose = () => {
            this.hideLandscapeModal()
            this.orientationOpenPending = false
        }

        const LandscapeModalClass = ReactComponentRegistry.get("MessagingLandscapeModal")
        const landscapeModalComponent = new LandscapeModalClass({ onClose }, container)
        this.landscapeModal = { component: landscapeModalComponent, container }
    }

    hideLandscapeModal(): void {
        if (!this.landscapeModal) {
            return
        }

        const { component, container } = this.landscapeModal
        if (typeof component.dispose === "function") {
            component.dispose()
        }
        document.body.removeChild(container)
        this.landscapeModal = undefined
    }

    private createInboxInstance(): void {
        if(!isPortrait()) {
            this.showLandscapeModal()
            return
        }

        // if instance already exists, show it
        const hasExistingInstance = this.instances.size > 0
        if (hasExistingInstance) {
            const existingInstanceId = this.instances.keys().next().value
            if (existingInstanceId === undefined) { return }
            this.showInstance(existingInstanceId)
            return
        }

        const messagingInstanceId = `messaging-${uuidv4()}`
        const viewType = isMobileDevice() ? View.Mobile : View.DesktopInbox

        this.createInstance({
            view: viewType,
            messagingInstanceId,
            onClose: () => {
                this.hideInstance(messagingInstanceId)
                messagingInboxClosed.fire()
            },
            ...messagingProps,
        })
    }

    destroyAllDesktopConversations(): void {
        const idsToDestroy: string[] = []
        for (const [id, instanceData] of this.instances) {
            if (instanceData.view === View.DesktopConversation) {
                idsToDestroy.push(id)
            }
        }
        idsToDestroy.forEach(id => this.destroyInstance(id))
    }
}

export const messagingManager = new MessagingManager()
